import React, { FC, useEffect, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import classNames from 'classnames';
import { Subject } from 'rxjs';
import { Tabs, Tab, Box } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import _ from 'lodash';

import { Modal } from '@shared/components/Modal';
import { Button } from '@shared/components/Button';
import { ToastMessage } from '@shared/components/Toast';
import { DeleteConfirmPopover } from '@shared/components/DeleteConfirmPopover';
import { useProcedureUIStore, useProceduresUIStore } from '@core/useStores';
import { SelectOption } from '@shared/components/SelectDropdown/Option';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Collapse } from '@mui/material';

import { useStyles } from './ProcedureModal.styles';
import { ProcedureDevices } from './components/ProcedureDevices';
import { ProcedureDetailsForm } from './components/ProcedureDetailsForm';
import { ImageCropModal } from './components/ImageCropModal';
import { ProcedureListingDevice } from 'Procedures/domain/Device';
import { messages } from 'Procedures/procedures.messages';
import { ThemeProps } from '@styles/theme';
import { VersionAssignmentPanel, VersionAssignmentPanelHandle } from '../VersionAssignmentModal';
import { ScoreDataPanel } from '../ScoreDataModal/ScoreDataModal';

import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@assets/close-icon.svg';
import FallbackProcedureIcon from '@assets/fallback-procedure-icon.png';

export const COMMON_SELECT_PROPS = {
    isSearchable: false,
    hideSelectedOptions: false,
    isClearable: false,
    isMulti: false,
    closeMenuOnSelect: true,
    components: { Option: SelectOption },
}

export type ProcedureModalProps = {
    isOpen: boolean,
    onRequestClose: () => void,
}

export const ProcedureModal: FC<ProcedureModalProps> = ({
    isOpen,
    onRequestClose,
}) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const procedureUIStore = useProcedureUIStore();
    const proceduresGridUIStore = useProceduresUIStore();

    // Ref for VersionAssignmentPanel imperative save
    const versionAssignmentRef = useRef<VersionAssignmentPanelHandle>(null);

    // create-mode state
    const [isDevicesListVisible, setDevicesListVisibility] = useState(true);
    const [formSubmitSubject$] = useState<Subject<{}>>(new Subject());

    // edit-mode state
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [activeEditTab, setActiveEditTab] = useState(0);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [cropSrc, setCropSrc] = useState<string | null>(null);
    const [cropMimeType, setCropMimeType] = useState<string>('image/jpeg');
    const [isCropModalOpen, setIsCropModalOpen] = useState(false);
    const [hasScoreData, setHasScoreData] = useState(false);
    const [detailsExpanded, setDetailsExpanded] = useState(true);
    const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLElement | null>(null);
    const deleteButtonRef = useRef<HTMLDivElement>(null);

    const isEditMode = !!procedureUIStore.procedureToEditId;

    useEffect(() => {
        const getProcedureData = async () => {
            if (isEditMode && procedureUIStore.procedureToEditId) {
                procedureUIStore.toggleLoading(true);

                const result = await procedureUIStore.loadProcedure(procedureUIStore.procedureToEditId);

                procedureUIStore.toggleLoading(false);

                if (result.success) {
                    const preparedDevices = result.data.devices.map((device: ProcedureListingDevice) => {
                        const { id, name, platform, type, procedureVersionId, hasScoreData: hsd } = device;

                        return {
                            _device: {
                                device: name,
                                platform,
                                deviceId: id,
                                type,
                                procedureVersionId,
                                hasScoreData: hsd,
                            },
                            uiId: _.uniqueId('tab_'),
                        }
                    });

                    procedureUIStore.setProcedureGeneralInfo(result.data);
                    procedureUIStore.setDevices(preparedDevices);

                    // Find the procedure from grid store (needed for versions/description/scoreData)
                    const procedureFromGrid = proceduresGridUIStore.procedures.find(
                        p => p.id === procedureUIStore.procedureToEditId
                    );

                    // Populate edit fields directly from loaded data
                    setEditName(result.data.name || '');
                    // Use the latest version's description first, fall back to procedure description
                    const sortedVersions = _.orderBy(procedureFromGrid?.versions || [], ['date'], ['desc']);
                    const latestVersionDescription = sortedVersions[0]?.description;
                    setEditDescription(latestVersionDescription || result.data.description || '');

                    // Check score data — use both API response and grid store as sources

                    const deviceWithScoreFromApi = result.data.devices?.find(
                        (d: ProcedureListingDevice) => d.hasScoreData
                    );
                    const deviceWithScoreFromGrid = procedureFromGrid?.versions
                        ?.flatMap((v: any) => v.devices ?? [])
                        ?.find((d: any) => d.hasScoreData);

                    const scoreDevice = deviceWithScoreFromApi || deviceWithScoreFromGrid;
                    if (scoreDevice) {
                        setHasScoreData(true);
                        const versionId = scoreDevice.procedureVersionId;
                        if (versionId) {
                            procedureUIStore.loadScoreData(versionId);
                        }
                    }

                    // Set initial image preview from grid data
                    if (procedureFromGrid?.procedurePreview?.src) {
                        setImagePreview(procedureFromGrid.procedurePreview.src);
                    }
                }
            }
        }

        getProcedureData();
        procedureUIStore.getToken();
    }, [isEditMode]);

    // Image dropzone for edit mode — opens crop modal before upload
    const { getRootProps: getImageRootProps, getInputProps: getImageInputProps } = useDropzone({
        accept: { 'image/jpeg': [], 'image/png': [], 'image/webp': [] },
        maxSize: 5 * 1024 * 1024,
        multiple: false,
        noKeyboard: true,
        onDropAccepted: (files: File[]) => {
            const rawUrl = URL.createObjectURL(files[0]);
            setCropSrc(rawUrl);
            setCropMimeType(files[0].type || 'image/jpeg');
            setIsCropModalOpen(true);
        },
        onDropRejected: () => {
            toast.error(
                <ToastMessage message={messages['procedureModal.image.upload.error']} type='error' />
            );
        },
    });

    const handleCropComplete = (blob: Blob, previewUrl: string) => {
        // Revoke the raw src URL to avoid memory leaks
        if (cropSrc) {URL.revokeObjectURL(cropSrc);}
        setCropSrc(null);
        setIsCropModalOpen(false);
        setImagePreview(previewUrl);

        const mimeToExt: Record<string, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };
        const ext = mimeToExt[blob.type] ?? 'jpg';
        const croppedFile = new File([blob], `procedure-image.${ext}`, { type: blob.type });
        procedureUIStore.uploadProcedureImage(croppedFile).then((result: any) => {
            if (result?.success) {
                toast.success(
                    <ToastMessage message={messages['procedureModal.image.upload.success']} type='success' />
                );
                proceduresGridUIStore.load();
            } else {
                URL.revokeObjectURL(previewUrl);
                setImagePreview(null);
                toast.error(
                    <ToastMessage message={messages['procedureModal.image.upload.error']} type='error' />
                );
            }
        });
    };

    const handleCropCancel = () => {
        if (cropSrc) {URL.revokeObjectURL(cropSrc);}
        setCropSrc(null);
        setIsCropModalOpen(false);
    };

    const handleRemoveImage = async (e: React.MouseEvent) => {
        e.stopPropagation();
        const result: any = await procedureUIStore.deleteProcedureImage();
        if (result?.success) {
            setImagePreview(null);
            toast.success(
                <ToastMessage message={messages['procedureModal.image.remove.success']} type='success' />
            );
            proceduresGridUIStore.load();
        } else {
            toast.error(
                <ToastMessage message={messages['procedureModal.image.remove.error']} type='error' />
            );
        }
    };

    const saveAll = async () => {
        const info = procedureUIStore.procedureGeneralInfo;
        const deviceIds = _.compact(
            procedureUIStore.devices.map((d: any) => d?._device?.deviceId)
        );

        procedureUIStore.toggleLoading(true);

        // Save procedure details (no individual toast)
        const result = await procedureUIStore.editProcedure({
            id: procedureUIStore.procedureToEditId!,
            name: editName,
            description: editDescription,
            deviceIds,
            groupIds: info?.groupIds?.slice() || [],
            userIds: info?.userIds?.slice() || [],
            ownership: info?.ownership || '',
            token: procedureUIStore.processToken || '',
        });

        procedureUIStore.toggleLoading(false);

        if (result?.success) {
            procedureUIStore.setProcedureGeneralInfo({ ...info!, name: editName, description: editDescription });
            proceduresGridUIStore.setParams({ page: 0 });
            proceduresGridUIStore.load();
            proceduresGridUIStore.loadStatistics();
        }

        // Save version assignments silently (no individual toast)
        if (versionAssignmentRef.current) {
            await versionAssignmentRef.current.save(true);
        }

        // Save score data if loaded
        if (hasScoreData) {
            await procedureUIStore.saveScoreData();
        }

        // Single unified success notification
        toast.success(
            <ToastMessage message={messages['procedureDetailsForm.update.success']} type='success' />
        );
    };

    const onDeleteProcedure = () => {
        setDeleteAnchorEl(deleteButtonRef.current);
    };

    const onDeleteProcedureConfirm = async () => {
        setDeleteAnchorEl(null);
        procedureUIStore.toggleProcedureModalOpen(false);
        const result = await procedureUIStore.deleteProcedure();
        if (result.success) {
            toast.success(
                <ToastMessage message={messages['proceduresPage.delete.success.toast']} type='success' />
            );
            proceduresGridUIStore.setParams({ page: 0 });
            proceduresGridUIStore.load();
            proceduresGridUIStore.loadStatistics();
        }
        procedureUIStore.cleanUpProcedureModal();
    };

    const submitForm = () => formSubmitSubject$.next({});

    // ── Edit mode render ──────────────────────────────────────────────────────
    if (isEditMode) {
        // Lookup procedure type from grid store
        const procedureFromGrid = proceduresGridUIStore.procedures.find(
            p => p.id === procedureUIStore.procedureToEditId
        );

        return (
            <>
                <Modal
                    isOpen={isOpen}
                    onRequestClose={onRequestClose}
                    className={styles.editModal}
                    shouldCloseOnOverlayClick={false}
                    shouldCloseOnEsc
                    noPadding
                >
                    <Observer>
                        {() => (
                            <div className={styles.editModalContainer}>
                                {/* ── Gradient header (title + close) ── */}
                                <div className={styles.editModalHeader}>
                                    <div className={styles.editHeaderContent}>
                                        <div className={styles.editHeaderTextBlock}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' as const }}>
                                                {procedureUIStore.isLoading && !procedureUIStore.procedureGeneralInfo ? (
                                                    <Skeleton variant="text" width={220} height={28} sx={{ bgcolor: 'rgba(255,255,255,0.18)', borderRadius: 1 }} />
                                                ) : (
                                                    <h2 className={styles.h2} style={{ color: 'white', fontSize: 21, margin: 0 }}>
                                                        {procedureUIStore.procedureGeneralInfo?.name || ''}
                                                    </h2>
                                                )}
                                                {!procedureUIStore.isLoading && procedureFromGrid?.type && (
                                                    <span className={styles.editHeaderTypeChip}>{procedureFromGrid.type}</span>
                                                )}
                                            </div>
                                            {procedureUIStore.isLoading && procedureUIStore.procedureGeneralInfo && (
                                                <CircularProgress size={14} style={{ color: 'rgba(255,255,255,0.7)', marginTop: 4 }} />
                                            )}
                                        </div>
                                    </div>
                                    <div className={styles.editHeaderRight}>
                                        <div className={styles.editCloseBtn} onClick={onRequestClose}>
                                            <img src={CloseIcon} />
                                        </div>
                                    </div>
                                </div>

                                {/* ── Scrollable body ── */}
                                <div className={styles.editModalBody}>
                                    {/* Details section: image (left) + fields (right) */}
                                    <div className={styles.editSection}>
                                        <div
                                            className={styles.editSectionTitle}
                                            onClick={() => setDetailsExpanded(e => !e)}
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                        >
                                        Details
                                            <ExpandMoreIcon style={{
                                                fontSize: 14,
                                                transition: 'transform 0.2s ease',
                                                transform: detailsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                marginLeft: 4,
                                                color: 'inherit',
                                            }} />
                                        </div>
                                        <Collapse in={detailsExpanded}>
                                            {procedureUIStore.isLoading && !procedureUIStore.procedureGeneralInfo ? (
                                                <div className={styles.editDetailsRow}>
                                                    <Skeleton variant="rectangular" width={220} height={140} sx={{ borderRadius: 1, flexShrink: 0 }} />
                                                    <Box sx={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        <Skeleton variant="text" width={80} height={18} />
                                                        <Skeleton variant="rectangular" height={44} sx={{ borderRadius: 1 }} />
                                                        <Skeleton variant="text" width={100} height={18} sx={{ mt: 1 }} />
                                                        <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 1 }} />
                                                    </Box>
                                                </div>
                                            ) : (
                                                <div className={styles.editDetailsRow}>
                                                    {/* Procedure image — click/drop to upload */}
                                                    <div className={styles.editProcedureImageWrapper}>
                                                        <div
                                                            className={styles.editProcedureImage}
                                                            title={messages['procedureModal.image.clickUpload']}
                                                            {...getImageRootProps()}
                                                        >
                                                            <input {...getImageInputProps()} />
                                                            {imagePreview ? (
                                                                <img
                                                                    className={styles.editProcedureImageThumb}
                                                                    src={imagePreview}
                                                                    alt="procedure"
                                                                />
                                                            ) : (
                                                                <img
                                                                    className={styles.editProcedureImagePlaceholder}
                                                                    src={FallbackProcedureIcon}
                                                                    alt="placeholder"
                                                                />
                                                            )}
                                                            <div className={styles.editProcedureImageOverlay}>
                                                                <CloudUploadOutlinedIcon style={{ fontSize: 28 }} />
                                                                <span>{messages['procedureModal.image.clickUpload']}</span>
                                                            </div>
                                                        </div>
                                                        {imagePreview && (
                                                            <div
                                                                className={styles.editProcedureImageRemoveBtn}
                                                                title="Remove image"
                                                                onClick={handleRemoveImage}
                                                            >
                                                                <ClearIcon fontSize="inherit" />
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Fields */}
                                                    <div className={styles.editDetailsFields}>
                                                        <div className={styles.editField}>
                                                            <label className={styles.editInputLabel}>
                                                                {messages['procedureModal.name.label']}
                                                            </label>
                                                            <input
                                                                className={styles.editInput}
                                                                value={editName}
                                                                onChange={e => setEditName(e.target.value)}
                                                            />
                                                        </div>
                                                        <div className={styles.editField}>
                                                            <label className={styles.editInputLabel}>
                                                                {messages['procedureModal.description.label']}
                                                            </label>
                                                            <textarea
                                                                className={classNames(styles.editInput, styles.editTextarea)}
                                                                value={editDescription}
                                                                onChange={e => setEditDescription(e.target.value)}
                                                                rows={3}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Collapse>
                                    </div>

                                    {/* Tabs section: Assign Versions | Score Data */}
                                    <div className={styles.editTabsSection}>
                                        {procedureUIStore.isLoading && !procedureUIStore.procedureGeneralInfo ? (
                                            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                <Skeleton variant="text" width={180} height={22} sx={{ mb: 1 }} />
                                                {[1, 2, 3].map(i => (
                                                    <Box key={i} sx={{ border: '1.5px solid #e8e8f0', borderRadius: 2, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                            <Skeleton variant="rounded" width={52} height={26} />
                                                            <Skeleton variant="text" width={90} height={18} />
                                                            <Skeleton variant="text" width={60} height={18} sx={{ ml: 'auto' }} />
                                                        </Box>
                                                        <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
                                                        <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <>
                                                <Tabs
                                                    value={activeEditTab}
                                                    onChange={(_: any, v: number) => setActiveEditTab(v)}
                                                    indicatorColor="primary"
                                                    textColor="primary"
                                                    className={styles.editTabs}
                                                >
                                                    <Tab label={messages['versionAssignmentModal.title']} />
                                                    {hasScoreData && <Tab label={messages['scoreDataModal.title']} />}
                                                </Tabs>

                                                <div hidden={activeEditTab !== 0} className={styles.editTabContent}>
                                                    {procedureUIStore.procedureToEditId && (
                                                        <VersionAssignmentPanel
                                                            ref={versionAssignmentRef}
                                                            selectedProcedureId={procedureUIStore.procedureToEditId}
                                                            hideSaveButton
                                                        />
                                                    )}
                                                </div>

                                                {hasScoreData && (
                                                    <div hidden={activeEditTab !== 1} className={styles.editTabContent}>
                                                        <ScoreDataPanel embedded />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* Footer: Delete | [Close] [Save] */}
                                    <div className={styles.editBodyFooter}>
                                        <div ref={deleteButtonRef}>
                                            <Button
                                                onClick={onDeleteProcedure}
                                                color="error"
                                                startIcon={<ClearIcon />}
                                                data-testid="prodecure-modal-delete"
                                            >
                                                {messages['procedureModal.procedure.delete']}
                                            </Button>
                                            <DeleteConfirmPopover
                                                anchorEl={deleteAnchorEl}
                                                onConfirm={onDeleteProcedureConfirm}
                                                onCancel={() => setDeleteAnchorEl(null)}
                                                message="Are you sure you want to delete this procedure? This action cannot be undone."
                                                confirmText="Delete procedure"
                                            />
                                        </div>
                                        <div className={styles.editFooterRight}>
                                            <Button onClick={onRequestClose} color="secondary">
                                                {messages['procedureModal.close']}
                                            </Button>
                                            <Button
                                                onClick={saveAll}
                                                disabled={procedureUIStore.isLoading}
                                            >
                                                {messages['procedureModal.saveDetails']}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Observer>
                </Modal>

                {/* Image crop modal — MUI Dialog renders into a portal above the procedure modal */}
                {cropSrc && (
                    <ImageCropModal
                        isOpen={isCropModalOpen}
                        imageSrc={cropSrc}
                        mimeType={cropMimeType}
                        onClose={handleCropCancel}
                        onCropComplete={handleCropComplete}
                    />
                )}
            </>
        );
    }

    // ── Create mode render (unchanged) ───────────────────────────────────────
    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            className={styles.modal}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc
        >
            <Observer>
                {() => (
                    <div className={styles.modalHeading}>
                        <h2 className={styles.h2}>
                            {messages['procedureModal.newProcedure']}
                        </h2>

                        {
                            (procedureUIStore.isTokenLoading || procedureUIStore.isLoading) && (
                                <CircularProgress className={styles.loader} color='inherit' size={24} />
                            )
                        }
                    </div>
                )}
            </Observer>

            <div className={styles.processStepsWrapper}>
                <span
                    onClick={() => setDevicesListVisibility(true)}
                    className={
                        classNames(styles.stepName,
                            { [styles.stepNameActive]: isDevicesListVisible },
                        )
                    }
                >
                    {messages['procedureModal.devices']}
                </span>

                <span className={styles.longDash}></span>

                <span
                    onClick={() =>
                        !procedureUIStore.isNextStepDisabled && setDevicesListVisibility(false)
                    }
                    className={
                        classNames(styles.stepName,
                            { [styles.stepNameActive]: !isDevicesListVisible },
                        )
                    }
                >
                    {messages['procedureModal.procedureDetails']}
                </span>
            </div>

            <ProcedureDevices
                isEditMode={false}
                className={classNames({ [styles.hidden]: !isDevicesListVisible })}
            />

            <ProcedureDetailsForm
                submitSubject={formSubmitSubject$}
                className={classNames({ [styles.hidden]: isDevicesListVisible })}
            />

            <div className={classNames(styles.buttons, styles.end)}>
                <div>
                    <Button
                        onClick={onRequestClose}
                        className={styles.button} color="secondary"
                    >
                        {messages['procedureModal.cancel']}
                    </Button>

                    {
                        isDevicesListVisible
                            ? (
                                <Observer>
                                    {() => (
                                        <Button
                                            className={styles.button}
                                            onClick={() => setDevicesListVisibility(false)}
                                            disabled={procedureUIStore.isNextStepDisabled}
                                        >
                                            {messages['procedureModal.next']}
                                        </Button>
                                    )}
                                </Observer>
                            )
                            : (
                                <Button
                                    className={styles.button}
                                    onClick={submitForm}
                                >
                                    {messages['procedureModal.save']}
                                </Button>
                            )
                    }
                </div>
            </div>
        </Modal>
    )
}
